import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveEncrypted, getAllEncrypted, deleteData, clearStore, getAppState, setAppState, STORES } from '../utils/storage';
import { ArrowLeft, Plus, Trash, PaperPlaneRight, ShieldCheck, Phone } from 'phosphor-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Safety = () => {
  const [contacts, setContacts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '' });
  const [autoAlerts, setAutoAlerts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { encryptionKey } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadContacts();
    loadAutoAlertsPreference();
  }, [encryptionKey]);

  const loadAutoAlertsPreference = async () => {
    try {
      const stored = await getAppState('auto_checkin_alerts');
      setAutoAlerts(Boolean(stored));
    } catch (error) {
      console.error('Failed to load auto-alerts preference:', error);
    }
  };

  const handleAutoAlertsToggle = async (enabled) => {
    setAutoAlerts(enabled);
    try {
      await setAppState('auto_checkin_alerts', enabled);
      if (!enabled) {
        // Turning OFF clears any pending follow-up events (no user-authorized
        // action currently depends on retaining them).
        await clearStore(STORES.FOLLOW_UPS);
        toast('Auto Check-In Alerts turned off', {
          description: 'Any pending follow-ups have been cleared.',
        });
      } else {
        toast('Auto Check-In Alerts enabled', {
          description: 'Vela can now leave a minimal follow-up marker (no message content) when a conversation feels especially intense.',
        });
      }
    } catch (error) {
      console.error('Failed to update auto-alerts:', error);
    }
  };

  const loadContacts = async () => {
    if (!encryptionKey) return;

    setIsLoading(true);
    try {
      const loadedContacts = await getAllEncrypted(STORES.CONTACTS, encryptionKey).catch(() => []);
      setContacts(loadedContacts);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    
    if (!newContact.name || !newContact.phone) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const contact = {
        id: Date.now(),
        name: newContact.name,
        phone: newContact.phone,
        consentStatus: 'pending',
        addedAt: new Date().toISOString(),
      };

      await saveEncrypted(STORES.CONTACTS, contact, encryptionKey);
      setContacts(prev => [...prev, contact]);
      setNewContact({ name: '', phone: '' });
      setShowAddForm(false);
      
      toast.success('Contact added - consent request sent', {
        description: 'They will need to confirm participation via SMS',
      });
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Remove this contact?')) return;

    try {
      await deleteData(STORES.CONTACTS, id);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Contact removed');
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  const sendCheckIn = async (contact) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notify`, {
        contact_name: contact.name,
        contact_email: contact.phone, // Using phone field
      });
      toast.success(`Check-in request sent to ${contact.name}`);
    } catch (error) {
      toast.error('Failed to send check-in');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 noise-texture">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/chat')}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="back-to-chat-btn"
            >
              <ArrowLeft size={20} weight="duotone" />
            </button>
            <h1 className="font-serif font-semibold text-xl text-stone-800" data-testid="safety-title">Safety & Support</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Crisis Resources */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-primary">
          <div className="flex items-start gap-3">
            <ShieldCheck size={24} weight="duotone" className="text-primary flex-shrink-0" />
            <div>
              <h2 className="font-serif font-semibold text-lg text-stone-800 mb-2">Crisis Resources</h2>
              <div className="space-y-2 text-sm text-stone-600 mb-4">
                <p>
                  <strong>US Crisis Lifeline:</strong>{' '}
                  <a href="tel:988" className="text-primary hover:underline" data-testid="crisis-hotline-link">988</a>
                </p>
                <p>
                  <strong>Crisis Text Line:</strong> Text HOME to{' '}
                  <a href="sms:741741" className="text-primary hover:underline">741741</a>
                </p>
              </div>
              <div className="bg-stone-50 rounded-xl px-4 py-3 border border-stone-200">
                <p className="text-xs text-stone-600 leading-relaxed">
                  AraVela is a supportive companion, not emergency care. If you are in immediate danger or crisis, 
                  please contact emergency services or a crisis hotline.
                </p>
              </div>
              <p className="text-xs text-stone-500 mt-3">
                If you're in immediate danger, call 911 or go to your nearest emergency room.
              </p>
            </div>
          </div>
        </div>

        {/* Trusted Contacts */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="contacts-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-serif font-semibold text-lg text-stone-800">Trusted Contacts</h2>
              <p className="text-sm text-stone-500 mt-1">
                People who can check in on you when you need support
              </p>
            </div>
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-2 hover:bg-primary/20 transition-colors"
                data-testid="add-contact-btn"
              >
                <Plus size={16} weight="bold" />
                Add
              </button>
            )}
          </div>

          {/* Add Contact Form */}
          {showAddForm && (
            <form onSubmit={addContact} className="mb-6 p-4 bg-stone-50 rounded-xl" data-testid="add-contact-form">
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  className="w-full bg-white border-stone-200 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-lg px-4 py-2 text-sm transition-all"
                  data-testid="contact-name-input"
                />
                <input
                  type="tel"
                  placeholder="Mobile number"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className="w-full bg-white border-stone-200 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-lg px-4 py-2 text-sm transition-all"
                  data-testid="contact-phone-input"
                />
                <div className="text-xs text-stone-500 bg-stone-100 rounded-lg p-3">
                  A consent SMS will be sent. They must confirm participation before receiving any check-ins.
                  No emotional details or journal information is included.
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white rounded-lg px-4 py-2 text-sm hover:shadow-soft transition-all"
                  data-testid="save-contact-btn"
                >
                  Send Consent Request
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewContact({ name: '', phone: '' });
                  }}
                  className="bg-stone-200 text-stone-700 rounded-lg px-4 py-2 text-sm hover:bg-stone-300 transition-colors"
                  data-testid="cancel-add-contact-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Contacts List */}
          {isLoading ? (
            <div className="text-center py-8 text-stone-500">Loading...</div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              No trusted contacts yet. Add someone who can check in on you.
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map(contact => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                  data-testid={`contact-${contact.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone size={20} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">{contact.name}</p>
                      <p className="text-sm text-stone-500">{contact.phone}</p>
                      {contact.consentStatus === 'pending' && (
                        <p className="text-xs text-amber-600 mt-1">Awaiting consent confirmation</p>
                      )}
                      {contact.consentStatus === 'confirmed' && (
                        <p className="text-xs text-emerald-600 mt-1">✓ Confirmed</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => sendCheckIn(contact)}
                      className="flex items-center gap-2 bg-primary/10 text-primary rounded-lg px-3 py-2 text-sm hover:bg-primary/20 transition-colors"
                      data-testid={`send-checkin-${contact.id}`}
                    >
                      <PaperPlaneRight size={16} weight="duotone" />
                      Check-in
                    </button>
                    <button
                      onClick={() => deleteContact(contact.id)}
                      className="text-stone-400 hover:text-red-500 transition-colors p-2"
                      data-testid={`delete-contact-${contact.id}`}
                    >
                      <Trash size={18} weight="duotone" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Auto-Alerts Setting */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-stone-800 mb-1">Auto Check-In Alerts</h3>
              <p className="text-sm text-stone-600">
                If it ever sounds like you may be struggling deeply, Vela can gently suggest reaching out to your support circle.
                You'll always be asked first.
              </p>
              <p className="text-xs text-stone-500 mt-2 leading-relaxed">
                When this is on, AraVela may keep a minimal local marker
                (timestamp and intensity only — never your messages) so Vela
                can offer a gentle follow-up. Turning it off clears any
                pending markers. Your conversation content is still never
                stored unless you tap Save to Diary.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                checked={autoAlerts}
                onChange={(e) => handleAutoAlertsToggle(e.target.checked)}
                className="sr-only peer"
                data-testid="auto-alerts-toggle"
              />
              <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="text-center text-xs text-stone-400 py-4 space-y-2">
          <p>AraVela is not a substitute for professional mental health care.</p>
          <p>Check-in messages contain no personal details—only a generic request for support.</p>
          <p className="pt-2 text-stone-500">
            When conversations feel especially intense, Vela may gently check in later to see how you're doing.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Safety;